import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Star, ArrowRight, CalendarClock, Scale, ShieldCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ConsultationForm } from "./ConsultationForm";

const ASSESSMENTS = [
  {
    key: "standard_assessment", self: true, name: "Standard Assessment", price: "$1,500",
    lookback: "6-Month Lookback", vendors: "Up to 3 Vendors / Channels",
    statement: "An independent review of a smaller or more concentrated marketing program.",
    items: [
      "6-month marketing performance review", "Review of up to 3 vendors and/or channels",
      "Marketing spend reconciliation", "Vendor invoice review", "Vendor performance report review",
      "Lead and inquiry analysis", "Retained-case analysis", "Cost-per-retained-case where data allows",
      "Unexplained charges, gaps & discrepancies", "Vendor accountability findings",
      "Executive findings report", "Strategic recommendations", "Executive review consultation",
    ],
    button: "Start Your Assessment",
  },
  {
    key: "expanded_assessment", self: true, highlight: true, name: "Expanded Assessment", price: "$2,500",
    lookback: "12-Month Lookback", vendors: "Up to 5 Vendors / Channels",
    statement: "A deeper view of where your marketing dollars went and what they produced.",
    items: [
      "Everything in Standard, plus:", "Full 12-month historical review",
      "Analysis of up to 5 vendors / channels", "Year-over-year performance patterns",
      "Deeper vendor performance comparisons", "Marketing allocation analysis",
      "Lead-to-retained-case conversion analysis", "Long-term spending & performance trends",
      "Expanded findings & recommendations",
    ],
    button: "Start Your Assessment",
  },
  {
    key: "comprehensive_assessment", name: "Comprehensive Assessment", pricePrefix: "Starting at", price: "$3,500",
    lookback: "12–18 Month Lookback", vendors: "Up to 8 Vendors / Channels",
    statement: "For larger budgets, multiple agencies and complex marketing ecosystems.",
    items: [
      "12–18 months of marketing history", "Review of up to 8 vendors / channels",
      "Comprehensive spend reconciliation", "Vendor-by-vendor performance analysis",
      "Cross-channel comparison", "Lead-source attribution review",
      "Intake & retention performance where data allows", "Cost-per-lead & cost-per-retained-case analysis",
      "Duplicated / unsupported expense identification", "Marketing efficiency analysis",
      "Vendor accountability scorecard", "Budget allocation recommendations",
      "Comprehensive executive report", "Executive findings presentation / strategy session",
    ],
    button: "Request Comprehensive Assessment",
  },
  {
    key: "enterprise_assessment", name: "Enterprise Assessment", price: "Custom",
    lookback: "Custom Lookback", vendors: "9+ Vendors / Channels",
    statement: "For large or complex marketing operations.",
    items: [
      "Custom historical lookback", "9+ marketing vendors / channels",
      "Multi-office / multi-market analysis", "Large-scale spend reconciliation",
      "Complex attribution analysis", "Vendor & agency portfolio analysis",
      "Intake & retention analysis", "Executive-level findings", "Custom reporting",
      "Leadership presentation", "Strategic recommendations",
    ],
    button: "Request Custom Assessment",
  },
];

const PROCESS = [
  { n: "1", t: "Submit", d: "The firm securely provides relevant marketing invoices, vendor reports, lead information and retained-case data." },
  { n: "2", t: "Reconcile", d: "RetainProof organizes and reconciles the firm's marketing spend, vendor-reported activity and actual outcomes." },
  { n: "3", t: "Analyze", d: "We identify performance gaps, discrepancies, attribution problems, inefficiencies and areas requiring additional scrutiny." },
  { n: "4", t: "Report", d: "Firm leadership receives an executive findings report with clear observations and recommendations." },
];

const ONGOING = [
  { icon: CalendarClock, name: "Quarterly Performance Review", price: "Starting at $1,000", unit: "per review",
    d: "Periodically compare current performance against the findings from your original assessment.",
    cta: "Ask About Quarterly Reviews" },
  { icon: Scale, name: "Annual Performance Review", price: "Starting at $2,000", unit: "per year",
    d: "An annual marketing accountability checkup with updated findings and recommendations.",
    cta: "Ask About Annual Reviews" },
  { icon: ShieldCheck, name: "Executive Marketing Oversight", price: "Starting at $3,500", unit: "per month",
    badge: "Available by consultation",
    d: "A high-touch, custom advisory and oversight engagement providing an independent layer between firm leadership and marketing vendors.",
    cta: "Discuss Executive Oversight" },
];

const FAQS = [
  ["Do I have to sign up for a subscription?", "No. A Marketing Performance Assessment is a one-time engagement. No long-term subscription is required. Some firms later choose an optional Quarterly or Annual review, or Executive Oversight — but that is entirely up to you."],
  ["How is pricing determined?", "Pricing is based on the size of the review: the lookback period, the number of vendors and channels analyzed, and the overall complexity of your marketing program."],
  ["Is my firm's data secure?", "Yes. RetainProof uses encryption in transit (TLS), role-based access controls, hashed passwords, optional two-factor authentication, per-firm data isolation, and activity audit logs."],
  ["Which systems do you work with?", "We work with the reports and data your firm and its vendors already produce — call-tracking such as CallRail, ad platforms such as Meta Ads and Google Ads, and case management such as Clio."],
  ["How does billing work?", "Assessments are administered by CMD Marketing Enterprises LLC and paid securely online for the Standard and Expanded tiers. Comprehensive, Enterprise and ongoing services are quoted and invoiced based on scope."],
  ["What do we receive?", "An independent executive findings report with clear observations and recommendations, plus an executive review consultation to walk through the results."],
];

export default function Billing() {
  const [current, setCurrent] = useState(null);
  const [consult, setConsult] = useState({ open: false, type: "general_consultation", plan: "", title: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then((r) => setCurrent(r.data)).catch(() => {});
  }, []);

  const openConsult = (type, plan, title) => setConsult({ open: true, type, plan, title });

  const startCheckout = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to start checkout");
      setBusy("");
    }
  };

  return (
    <div className="pb-8">
      <p className="font-mono-data text-[11px] text-zinc-500 mb-4" data-testid="billing-brand-line">
        RetainProof™ is a product of CMD Marketing Enterprises LLC, delivered through its CMD+DesignLab brand.
      </p>

      {/* Header */}
      <div className="mb-8 max-w-3xl">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Marketing Performance Assessment</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light mt-1" data-testid="billing-heading">Know What Your Marketing Is Actually Producing.</h1>
        <p className="text-base text-zinc-600 mt-3">
          RetainProof independently reconciles what your firm spends, what your marketing vendors report, and the cases your firm actually retains. Assessments are one-time engagements — priced by the size of the review.
        </p>
        {current?.current_plan && current.current_plan !== "none" && (
          <p className="text-sm text-zinc-500 mt-3" data-testid="current-plan">
            Your firm currently has an active <span className="font-medium capitalize">{current.current_plan}</span> engagement
            {current.subscription_status ? ` (${current.subscription_status}).` : "."}
          </p>
        )}
      </div>

      {/* How pricing works */}
      <div className="mb-10 border border-zinc-200 bg-white p-6" data-testid="how-pricing-works">
        <h2 className="font-display text-2xl font-light">Simple pricing based on the size of the review</h2>
        <div className="mt-5 grid sm:grid-cols-3 gap-6">
          {[
            ["Lookback Period", "How much historical marketing activity we analyze."],
            ["Number of Vendors & Channels", "How many agencies, lead generators, platforms and marketing sources are included."],
            ["Complexity", "The volume of invoices, reports, lead data, retained-case information, markets and attribution sources."],
          ].map(([t, d]) => (
            <div key={t}>
              <p className="font-display text-lg">{t}</p>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Assessment tiers */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {ASSESSMENTS.map((t) => (
          <div key={t.key}
            className={`relative border p-6 flex flex-col bg-white ${t.highlight ? "border-[#0B1F33] border-2 shadow-lg" : "border-zinc-200"}`}
            data-testid={`assessment-${t.key}`}>
            {t.highlight && (
              <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#D5A44A] text-black text-[10px] font-mono-data uppercase tracking-widest px-2 py-1" data-testid="assessment-most-popular">
                <Star className="h-3 w-3 fill-black" /> Most Popular
              </span>
            )}
            <h3 className="font-display text-2xl leading-tight">{t.name}</h3>
            <p className="mt-3">
              {t.pricePrefix && <span className="block text-xs text-zinc-500">{t.pricePrefix}</span>}
              <span className="font-display text-4xl">{t.price}</span>
            </p>
            <div className="mt-4 border-y border-zinc-200 py-3 space-y-1">
              <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#B54747]">{t.lookback}</p>
              <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#0E7C7B]">{t.vendors}</p>
            </div>
            <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{t.statement}</p>
            <ul className="mt-4 space-y-2 flex-1">
              {t.items.map((it) => {
                const isHeader = it.endsWith("plus:");
                return (
                  <li key={it} className={`flex gap-2 text-sm ${isHeader ? "text-zinc-900 font-medium" : "text-zinc-600"}`}>
                    {!isHeader && <Check className="h-4 w-4 text-[#156F52] shrink-0 mt-0.5" />}{it}
                  </li>
                );
              })}
            </ul>
            <Button
              onClick={() => t.self ? startCheckout(t.key) : openConsult("private_consultation", t.name, t.button)}
              disabled={busy === t.key}
              data-testid={`assessment-cta-${t.key}`}
              className={`mt-6 w-full rounded-none h-11 text-xs sm:text-sm ${t.highlight ? "bg-[#D5A44A] text-black hover:bg-[#B8923D]" : "bg-[#0B1F33] text-white hover:bg-zinc-800"}`}
            >
              {busy === t.key ? "Starting…" : t.button}
            </Button>
          </div>
        ))}
      </div>

      {/* Process */}
      <div className="mt-14" data-testid="assessment-process">
        <h2 className="font-display text-3xl font-light mb-6">The assessment process</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((s) => (
            <div key={s.n} className="border-t-2 border-[#D5A44A] bg-white p-5" data-testid={`process-step-${s.n}`}>
              <p className="font-display text-3xl text-[#D5A44A]/50">{s.n}</p>
              <p className="font-display text-lg mt-1">{s.t}</p>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* After your assessment */}
      <div className="mt-14" data-testid="after-assessment">
        <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#D5A44A]">After your assessment</p>
        <h2 className="font-display text-3xl font-light mt-2 mb-2">Optional ongoing services</h2>
        <p className="text-sm text-zinc-600 mb-6 max-w-3xl">The assessment gives your firm a clear benchmark. RetainProof can also help you measure what happens after changes are made.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {ONGOING.map((s) => (
            <div key={s.name} className="border border-zinc-200 bg-white p-6 flex flex-col" data-testid={`ongoing-${s.name.toLowerCase().replace(/\W/g, "")}`}>
              <s.icon className="h-6 w-6 text-[#D5A44A]" />
              <h3 className="font-display text-xl mt-3 leading-tight">{s.name}</h3>
              {s.badge && <span className="mt-2 inline-block w-fit border border-[#0E7C7B]/40 text-[#0E7C7B] text-[10px] font-mono-data uppercase tracking-widest px-2 py-0.5">{s.badge}</span>}
              <p className="mt-3"><span className="font-display text-2xl">{s.price}</span><span className="text-zinc-500 text-sm"> {s.unit}</span></p>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">{s.d}</p>
              <Button onClick={() => openConsult("private_consultation", s.name, s.cta)} className="mt-5 w-full rounded-none h-11 bg-[#0B1F33] text-white hover:bg-zinc-800 text-xs sm:text-sm" data-testid={`ongoing-cta-${s.name.toLowerCase().replace(/\W/g, "")}`}>
                {s.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-14 max-w-3xl">
        <h2 className="font-display text-3xl font-light mb-4">Frequently asked</h2>
        <Accordion type="single" collapsible className="border border-zinc-200 bg-white divide-y divide-zinc-200" data-testid="faq">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`faq-${i}`} className="border-none">
              <AccordionTrigger className="px-5 text-left text-sm" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="px-5 text-sm text-zinc-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Closing */}
      <div className="mt-16 border border-[#0B1F33] bg-[#0B1F33] text-white p-10 text-center" data-testid="billing-closing">
        <h2 className="font-display text-3xl sm:text-4xl font-light max-w-3xl mx-auto leading-tight">
          You don't need more marketing data. You need a clearer view of what it means.
        </h2>
        <p className="mt-4 text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          RetainProof helps firm leadership independently evaluate marketing spend, vendor performance and retained-case outcomes.
        </p>
        <Button
          onClick={() => openConsult("general_consultation", "", "Schedule a RetainProof Consultation")}
          className="mt-8 rounded-none bg-[#D5A44A] text-black hover:bg-[#B8923D] h-12 px-8 group"
          data-testid="closing-cta"
        >
          Schedule a Consultation
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
        <p className="mt-8 font-mono-data text-[11px] text-zinc-500">
          Provided by CMD Marketing Enterprises LLC through CMD+DesignLab
        </p>
      </div>

      <ConsultationForm
        open={consult.open}
        onClose={() => setConsult((c) => ({ ...c, open: false }))}
        requestType={consult.type}
        preferredPlan={consult.plan}
        title={consult.title}
      />
    </div>
  );
}
